// Service 9 — Property purchase, leasing and title transfer for foreigners in Thailand.
// Category definitions only (client-bundle safe, no corpus import).

export type PropertyFaqCategory =
  | "condo-foreign-quota"
  | "land-house-ownership"
  | "lease-usufruct-superficies"
  | "title-deeds-due-diligence"
  | "transfer-taxes-fees"
  | "poa-remote-transfer"
  | "company-holding-nominee"
  | "spouse-inheritance"
  | "developer-contracts-escrow"
  | "phuket-practical";

export interface PropertyCategoryDef {
  slug: PropertyFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const PROPERTY_FAQ_CATEGORIES: PropertyCategoryDef[] = [
  {
    slug: "condo-foreign-quota",
    titleTh: "ซื้อคอนโดในชื่อชาวต่างชาติ — โควตา 49% และหลักฐานการโอนเงินจากต่างประเทศ",
    titleEn: "Buying a Condominium in a Foreign Name — the 49% Quota and Foreign Currency Evidence",
    descTh:
      "เงื่อนไขตามพระราชบัญญัติอาคารชุด พ.ศ. 2522 มาตรา 19 และ 19 ทวิ โควตากรรมสิทธิ์ต่างชาติไม่เกิน 49% ของพื้นที่ห้องชุดทั้งอาคาร หนังสือรับรองการนำเงินตราต่างประเทศเข้าประเทศ (FET / Credit Advice) และหนังสือรับรองอัตราส่วนกรรมสิทธิ์จากนิติบุคคลอาคารชุด",
    descEn:
      "Requirements under the Condominium Act B.E. 2522 sections 19 and 19 bis, the 49% foreign-ownership quota of total unit floor area, the Foreign Exchange Transaction form or credit advice, and the juristic person's foreign-quota certificate.",
    matchTh: /(คอนโด|ห้องชุด|อาคารชุด|โควตา|49%|49 %|FET|Foreign Exchange|Credit Advice|นิติบุคคลอาคารชุด|นำเงินเข้า)/i,
    matchEn: /(condominium|condo|unit|49%|foreign quota|FET|foreign exchange transaction|credit advice|juristic person|remit)/i,
  },
  {
    slug: "land-house-ownership",
    titleTh: "ที่ดินและบ้านเดี่ยว — ข้อจำกัดกรรมสิทธิ์ของชาวต่างชาติ",
    titleEn: "Land and Houses — Foreign Ownership Restrictions",
    descTh:
      "หลักห้ามชาวต่างชาติถือกรรมสิทธิ์ที่ดินตามประมวลกฎหมายที่ดิน ข้อยกเว้นตามมาตรา 96 ทวิ (นำเงินเข้า 40 ล้านบาท ไม่เกิน 1 ไร่เพื่อที่อยู่อาศัย) สิทธิถือที่ดินของผู้ได้รับส่งเสริมการลงทุน BOI และการแยกกรรมสิทธิ์สิ่งปลูกสร้างออกจากที่ดิน",
    descEn:
      "The general prohibition on foreign land ownership under the Land Code, the section 96 bis exception (THB 40 million inward investment, up to 1 rai for residential use), BOI-promoted land rights, and separating ownership of the building from the land.",
    matchTh: /(ที่ดิน|บ้านเดี่ยว|96 ทวิ|40 ล้าน|1 ไร่|BOI|กรรมสิทธิ์ที่ดิน|สิ่งปลูกสร้าง|นิคมอุตสาหกรรม)/i,
    matchEn: /(land|house|96 bis|40 million|one rai|1 rai|BOI|land ownership|building|industrial estate)/i,
  },
  {
    slug: "lease-usufruct-superficies",
    titleTh: "สัญญาเช่า 30 ปี สิทธิเก็บกิน สิทธิเหนือพื้นดิน และสิทธิอาศัย",
    titleEn: "30-Year Leases, Usufruct, Superficies and Habitation",
    descTh:
      "ทางเลือกที่ชาวต่างชาติใช้ได้อย่างถูกกฎหมายแทนการถือกรรมสิทธิ์ที่ดิน การเช่าตาม ป.พ.พ. มาตรา 538 และ 540 การจดทะเบียนต่อพนักงานเจ้าหน้าที่ ณ สำนักงานที่ดิน สิทธิเก็บกิน (ม.1417) สิทธิเหนือพื้นดิน (ม.1410) และข้อจำกัดของคำมั่นจะต่อสัญญา",
    descEn:
      "Lawful alternatives to land ownership: leases under Civil and Commercial Code sections 538 and 540, registration at the Land Office, usufruct (s.1417), superficies (s.1410), right of habitation, and the limits of renewal promises.",
    matchTh: /(เช่า|สัญญาเช่า|30 ปี|สิทธิเก็บกิน|สิทธิเหนือพื้นดิน|สิทธิอาศัย|ต่อสัญญา|จดทะเบียนการเช่า|1417|1410)/i,
    matchEn: /(lease|leasehold|30 year|usufruct|superficies|habitation|renewal|registered lease|1417|1410)/i,
  },
  {
    slug: "title-deeds-due-diligence",
    titleTh: "ประเภทเอกสารสิทธิและการตรวจสอบก่อนวางเงิน (Due Diligence)",
    titleEn: "Title Documents and Pre-Purchase Due Diligence",
    descTh:
      "ความต่างระหว่างโฉนดที่ดิน (น.ส.4) น.ส.3ก น.ส.3 ส.ค.1 และหนังสือกรรมสิทธิ์ห้องชุด (อ.ช.2) การตรวจสารบบที่ดิน ภาระจำยอง จำนอง อายัด ผังเมือง แนวเวนคืน ทางเข้าออก และการรุกล้ำ",
    descEn:
      "The difference between a Chanote (Nor Sor 4), Nor Sor 3 Gor, Nor Sor 3, Sor Kor 1 and a condominium unit title, plus title-file searches, servitudes, mortgages, injunctions, zoning, expropriation lines, legal access and encroachment.",
    matchTh: /(โฉนด|น\.ส\.3|น\.ส\.4|ส\.ค\.1|อ\.ช\.2|สารบบ|ภาระจำยอง|จำนอง|อายัด|ผังเมือง|เวนคืน|ทางเข้าออก|รุกล้ำ|ตรวจสอบ)/i,
    matchEn: /(chanote|nor sor|title deed|title search|servitude|mortgage|zoning|expropriat|access road|encroach|due diligence)/i,
  },
  {
    slug: "transfer-taxes-fees",
    titleTh: "ค่าธรรมเนียมและภาษีในวันโอนที่สำนักงานที่ดิน",
    titleEn: "Transfer Fees and Taxes at the Land Office",
    descTh:
      "ค่าธรรมเนียมจดทะเบียนโอน 2% ของราคาประเมิน อากรแสตมป์ 0.5% ภาษีธุรกิจเฉพาะ 3.3% เมื่อขายภายใน 5 ปี ภาษีเงินได้หัก ณ ที่จ่าย การแบ่งภาระระหว่างผู้ซื้อผู้ขาย และแคชเชียร์เช็คที่สำนักงานที่ดินยอมรับ",
    descEn:
      "The 2% transfer fee on appraised value, 0.5% stamp duty, 3.3% specific business tax on sales within five years, withholding income tax, how buyer and seller split the burden, and the cashier's cheques the Land Office accepts.",
    matchTh: /(ค่าธรรมเนียม|ภาษี|อากรแสตมป์|ธุรกิจเฉพาะ|หัก ณ ที่จ่าย|ราคาประเมิน|แคชเชียร์เช็ค|2%|0\.5%|3\.3%)/i,
    matchEn: /(transfer fee|tax|stamp duty|specific business tax|withholding|appraised value|cashier|2%|0\.5%|3\.3%)/i,
  },
  {
    slug: "poa-remote-transfer",
    titleTh: "โอนกรรมสิทธิ์โดยไม่ต้องบินมาไทย — หนังสือมอบอำนาจ ท.ด.21 / อ.ช.21",
    titleEn: "Transferring Without Flying to Thailand — Land Office Powers of Attorney",
    descTh:
      "การใช้แบบพิมพ์หนังสือมอบอำนาจของกรมที่ดิน (ท.ด.21 สำหรับที่ดิน / อ.ช.21 สำหรับห้องชุด) การรับรองลายมือชื่อโดย Notarial Services Attorney หรือสถานทูต การรับรองนิติกรณ์ต่อ และข้อควรระวังเรื่องการเว้นช่องว่างในแบบพิมพ์",
    descEn:
      "Using the Land Department's own power-of-attorney forms (Tor Dor 21 for land, Or Chor 21 for condominium units), signature certification by a Notarial Services Attorney or embassy, onward legalisation, and the rule against leaving blanks in the form.",
    matchTh: /(มอบอำนาจ|ท\.ด\.21|อ\.ช\.21|ตัวแทน|ไม่ต้องเดินทาง|รับรองลายมือชื่อ|notary|สถานทูต|นิติกรณ์|Apostille)/i,
    matchEn: /(power of attorney|POA|Tor Dor 21|Or Chor 21|attorney-in-fact|remote|notari|embassy|legalis|legaliz|apostille)/i,
  },
  {
    slug: "company-holding-nominee",
    titleTh: "ถือที่ดินผ่านบริษัทไทย และเส้นแบ่งเรื่องนอมินี",
    titleEn: "Holding Land Through a Thai Company and the Nominee Line",
    descTh:
      "เงื่อนไขที่บริษัทไทยถือที่ดินได้ การตรวจสอบแหล่งเงินของผู้ถือหุ้นไทยตามหนังสือสั่งการกรมที่ดิน ความรับผิดตามพระราชบัญญัติการประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 มาตรา 36 และมาตรา 113 ประมวลกฎหมายที่ดิน",
    descEn:
      "When a Thai company may hold land, Land Department scrutiny of Thai shareholders' source of funds, liability under section 36 of the Foreign Business Act B.E. 2542 and section 113 of the Land Code.",
    matchTh: /(บริษัทไทย|นอมินี|ผู้ถือหุ้น|มาตรา 36|113|ธุรกิจของคนต่างด้าว|โครงสร้างการถือหุ้น|แหล่งเงิน)/i,
    matchEn: /(thai company|nominee|shareholder|section 36|113|foreign business act|shareholding structure|source of funds)/i,
  },
  {
    slug: "spouse-inheritance",
    titleTh: "คู่สมรสไทยซื้อที่ดิน และการรับมรดกอสังหาริมทรัพย์ของชาวต่างชาติ",
    titleEn: "Thai Spouses Buying Land and Foreign Inheritance of Property",
    descTh:
      "หนังสือยืนยันของคู่สมรสต่างชาติว่าเงินที่ใช้ซื้อเป็นสินส่วนตัวของคู่สมรสไทย ตามหนังสือสั่งการกรมที่ดิน การรับมรดกที่ดินโดยชาวต่างชาติตามมาตรา 93 และ 94 ประมวลกฎหมายที่ดิน และการรับมรดกห้องชุดตามมาตรา 19 เบญจ",
    descEn:
      "The foreign spouse's declaration that the purchase money is the Thai spouse's personal property, foreign inheritance of land under Land Code sections 93 and 94, and inheritance of condominium units under section 19 quinquies.",
    matchTh: /(คู่สมรส|สินส่วนตัว|มรดก|ทายาท|พันธุกรรม|พินัยกรรม|มาตรา 93|19 เบญจ|จำหน่ายภายใน 1 ปี)/i,
    matchEn: /(spouse|personal property|inherit|heir|will|estate|section 93|19 quinquies|dispose within one year)/i,
  },
  {
    slug: "developer-contracts-escrow",
    titleTh: "สัญญาจะซื้อจะขายโครงการ เงินดาวน์ และการคุ้มครองผู้ซื้อ",
    titleEn: "Off-Plan Contracts, Deposits and Buyer Protection",
    descTh:
      "แบบสัญญามาตรฐาน อ.ช.22 ที่พระราชบัญญัติอาคารชุดกำหนด การคืนเงินเมื่อโครงการผิดสัญญา บัญชี Escrow ตามพระราชบัญญัติการดูแลผลประโยชน์ของคู่สัญญา พ.ศ. 2551 กองทุนส่วนกลางและค่าส่วนกลางที่ต้องชำระก่อนโอน",
    descEn:
      "The statutory off-plan contract form under the Condominium Act, refunds when a developer defaults, escrow accounts under the Escrow Act B.E. 2551, and sinking-fund and common-area fees payable before transfer.",
    matchTh: /(สัญญาจะซื้อจะขาย|โครงการ|เงินดาวน์|เงินจอง|Escrow|ดูแลผลประโยชน์|ค่าส่วนกลาง|กองทุน|อ\.ช\.22|ผิดสัญญา)/i,
    matchEn: /(off-plan|sale and purchase agreement|deposit|reservation|escrow|common area fee|sinking fund|default|developer)/i,
  },
  {
    slug: "phuket-practical",
    titleTh: "ปฏิบัติจริงที่ภูเก็ต — สำนักงานที่ดิน ล่าม และลำดับงานในวันโอน",
    titleEn: "Practical Phuket — Land Offices, Interpreters and Transfer-Day Sequence",
    descTh:
      "สำนักงานที่ดินจังหวัดภูเก็ตและสาขาถลาง การนัดคิว การใช้ล่ามและคำแปลเอกสารต่างประเทศ ลำดับงานในวันโอนตั้งแต่ตรวจโฉนดถึงรับสำเนาโฉนดหลังจดทะเบียน",
    descEn:
      "The Phuket Provincial Land Office and Thalang branch, booking slots, interpreters and translations of foreign documents, and the transfer-day sequence from title check to receiving the updated deed.",
    matchTh: /(ภูเก็ต|ถลาง|กะทู้|สำนักงานที่ดินจังหวัด|ล่าม|วันโอน|นัดคิว|ป่าตอง)/i,
    matchEn: /(phuket|thalang|kathu|provincial land office|interpreter|transfer day|appointment|patong)/i,
  },
];

export function getPropertyCategoryDef(slug: string): PropertyCategoryDef | undefined {
  return PROPERTY_FAQ_CATEGORIES.find((c) => c.slug === slug);
}